import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Clock, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

const OUTCOME = {
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
  deficient: { label: 'Deficient — action needed', cls: 'bg-red-100 text-red-800 border-red-200', Icon: AlertTriangle },
  pending: { label: 'Review in progress', cls: 'bg-slate-100 text-slate-700 border-slate-200', Icon: Clock },
};

/** Agent-facing compliance review status for a client folder (outcome + officer notes, no FINTRAC data). */
const FolderReviewStatus = ({ apiUrl, folderId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [showMissing, setShowMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folderId}/review-status`);
      if (r.ok) setData(await r.json());
    } catch (e) { console.error('review status failed', e); }
  }, [apiUrl, folderId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data || data.status === 'none') return null;

  if (data.status === 'for_review') {
    return (
      <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border border-orange-200 bg-orange-50 text-sm" data-testid="folder-review-status">
        <Clock className="h-4 w-4 text-orange-700 mt-0.5" />
        <div>
          <p className="font-semibold text-orange-900" data-testid="folder-review-state">
            {data.stale_review ? 'Package rebuilt — waiting for the compliance officer to re-review' : 'Final package submitted — waiting for compliance review'}
          </p>
          <p className="text-xs text-orange-700">Sent to the review queue on {new Date(data.package_created_at).toLocaleString()}.</p>
          {data.stale_review && data.review && <p className="text-xs text-orange-700 mt-1">Previous outcome: <b>{OUTCOME[data.review.outcome]?.label || data.review.outcome}</b>{data.review.review_notes ? ` — ${data.review.review_notes}` : ''}</p>}
        </div>
      </div>
    );
  }

  const rv = data.review || {};
  const oc = OUTCOME[rv.outcome] || OUTCOME.pending;
  return (
    <div className={`mt-3 p-3 rounded-lg border text-sm ${oc.cls}`} data-testid="folder-review-status">
      <div className="flex items-start gap-2">
        <oc.Icon className="h-4 w-4 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold flex items-center gap-2 flex-wrap">
            <ShieldCheck className="h-3.5 w-3.5" /> Compliance review: <span data-testid="folder-review-outcome">{oc.label}</span>
            <span className="text-[10px] font-normal opacity-80">rev {rv.revision} • {rv.reviewed_by} • {rv.reviewed_at ? new Date(rv.reviewed_at).toLocaleString() : ''}</span>
          </p>
          {rv.review_notes ? (
            <p className="mt-1 whitespace-pre-wrap" data-testid="folder-review-notes"><span className="font-semibold">Officer notes:</span> {rv.review_notes}</p>
          ) : (
            <p className="mt-1 text-xs opacity-80" data-testid="folder-review-notes">No notes from the compliance officer.</p>
          )}
          <p className="text-xs mt-1 opacity-90">{rv.verified_count} of {rv.total_items} checklist documents verified by the officer.</p>
          {rv.missing_items?.length > 0 && (
            <>
              <button type="button" onClick={() => setShowMissing(s => !s)} className="text-xs underline mt-1 flex items-center gap-1" data-testid="folder-review-toggle-missing">
                {showMissing ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {rv.missing_items.length} document(s) not verified
              </button>
              {showMissing && (
                <ul className="mt-1 text-xs list-disc pl-5 columns-1 sm:columns-2" data-testid="folder-review-missing-list">
                  {rv.missing_items.map(m => <li key={m}>{m}</li>)}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderReviewStatus;
